import type { LocationType, PriceBreakdown } from '@/types/order';

/* ── 다물품 할인 ── */

export interface MultiItemDiscount {
  discountRate: number;
  discountedPrice: number;
}

/**
 * 다물품 할인 계산
 * - 2개: 저렴한 물품 55% 할인
 * - 3개+: 가장 비싼 물품 정가, 두 번째 55% 할인, 나머지 70% 할인
 */
export function calculateMultiItemDiscounts(
  items: { uid: string; basePrice: number }[],
): Map<string, MultiItemDiscount> {
  const result = new Map<string, MultiItemDiscount>();

  if (items.length < 2) {
    items.forEach((item) => {
      result.set(item.uid, { discountRate: 0, discountedPrice: item.basePrice });
    });
    return result;
  }

  // 가격 내림차순 정렬
  const sorted = [...items].sort((a, b) => b.basePrice - a.basePrice);

  if (items.length === 2) {
    result.set(sorted[0].uid, { discountRate: 0, discountedPrice: sorted[0].basePrice });
    result.set(sorted[1].uid, {
      discountRate: 0.55,
      discountedPrice: Math.round(sorted[1].basePrice * 0.45),
    });
  } else {
    sorted.forEach((item, idx) => {
      if (idx === 0) {
        result.set(item.uid, { discountRate: 0, discountedPrice: item.basePrice });
      } else if (idx === 1) {
        result.set(item.uid, {
          discountRate: 0.55,
          discountedPrice: Math.round(item.basePrice * 0.45),
        });
      } else {
        result.set(item.uid, {
          discountRate: 0.7,
          discountedPrice: Math.round(item.basePrice * 0.3),
        });
      }
    });
  }

  return result;
}

/* ── 거리 할증 (티어별) ── */

/**
 * 직선거리(haversine) 기준 티어:
 * - 0 ~ 10km: 도로거리 = 직선 × 1.3, 1,800원/km
 * - 10 ~ 25km: 도로거리 = 직선 × 1.2, 1,500원/km
 * - 25km 초과: 도로거리 = 직선 × 1.15, 1,200원/km
 *
 * 3km 도로거리 무료 (티어1부터 차감), 이후 1km 단위 올림 후 요율 적용.
 */
const DISTANCE_TIERS: { maxStraightKm: number; multiplier: number; ratePerKm: number }[] = [
  { maxStraightKm: 10, multiplier: 1.3, ratePerKm: 1800 },
  { maxStraightKm: 25, multiplier: 1.2, ratePerKm: 1500 },
  { maxStraightKm: Infinity, multiplier: 1.15, ratePerKm: 1200 },
];
const FREE_ROAD_KM = 3;

export interface DistanceSurchargeResult {
  roadKm: number;
  surcharge: number;
}

export function calcDistanceSurcharge(straightKm: number): DistanceSurchargeResult {
  if (!Number.isFinite(straightKm) || straightKm <= 0) {
    return { roadKm: 0, surcharge: 0 };
  }

  const segments: { roadKm: number; rate: number }[] = [];
  let remaining = straightKm;
  let prevMax = 0;
  let totalRoadKm = 0;

  for (const tier of DISTANCE_TIERS) {
    const tierStraightWidth = tier.maxStraightKm - prevMax;
    const usedStraight = Math.min(remaining, tierStraightWidth);
    if (usedStraight <= 0) break;
    const segRoadKm = usedStraight * tier.multiplier;
    segments.push({ roadKm: segRoadKm, rate: tier.ratePerKm });
    totalRoadKm += segRoadKm;
    remaining -= usedStraight;
    prevMax = tier.maxStraightKm;
    if (remaining <= 0) break;
  }

  // 3km 도로거리 무료를 순서대로 각 티어 segment 에서 차감 → 남은 도로거리에 요율 적용
  let freeBudget = FREE_ROAD_KM;
  let surcharge = 0;
  for (const seg of segments) {
    const billableRoad = Math.max(0, seg.roadKm - freeBudget);
    freeBudget = Math.max(0, freeBudget - seg.roadKm);
    surcharge += Math.ceil(billableRoad) * seg.rate;
  }

  return {
    roadKm: Math.round(totalRoadKm * 10) / 10,
    surcharge,
  };
}

/**
 * 도로거리(km)를 직접 입력받아 거리할증 계산. × 1.3 보정 없이, 도로 km 자체에 티어별 요율 적용.
 * 티어 경계는 도로 km 기준 (직선 티어 × multiplier 와 일치): ≤13km(=10×1.3) / ≤30km(=25×1.2) / >30km.
 * 3km 도로거리 무료 차감 후 ceil → 요율 적용.
 */
export function calcDistanceSurchargeFromRoad(roadKm: number): number {
  if (!Number.isFinite(roadKm) || roadKm <= 0) return 0;
  const roadTiers = [
    { maxRoadKm: 13, ratePerKm: 1800 },
    { maxRoadKm: 30, ratePerKm: 1500 },
    { maxRoadKm: Infinity, ratePerKm: 1200 },
  ];
  let remaining = roadKm;
  let prevMax = 0;
  let freeBudget = FREE_ROAD_KM;
  let surcharge = 0;
  for (const tier of roadTiers) {
    const used = Math.min(remaining, tier.maxRoadKm - prevMax);
    if (used <= 0) break;
    const billable = Math.max(0, used - freeBudget);
    freeBudget = Math.max(0, freeBudget - used);
    surcharge += Math.ceil(billable) * tier.ratePerKm;
    remaining -= used;
    prevMax = tier.maxRoadKm;
    if (remaining <= 0) break;
  }
  return surcharge;
}

/* ── 위치 유형 보정 (집안 추가 / 주차장 할인) ── */

export interface LocationItemInput {
  uid: string;
  basePrice: number;
  locationInsideExtra: number;
  locationParkingDiscount: number;
}

export interface LocationAdjustments {
  /** INSIDE 모드일 때 양수, 그 외 0 */
  originSurcharge: number;
  /** PARKING 모드일 때 양수 (할인액), 그 외 0 */
  originDiscount: number;
  destSurcharge: number;
  destDiscount: number;
}

/**
 * 위치 유형별 보정 금액 합산. 각 물품의 location 값에 다물품 할인율을 동일하게 적용 후 합산.
 *
 * 예) 침대프레임 location 2000/3000, 다물품 0% → 집안 +2000 / 주차장 -3000
 *     매트리스 동일, 55% 할인 → 집안 +900 / 주차장 -1350
 */
export function calcLocationAdjustments(
  items: LocationItemInput[],
  originType: LocationType | null | undefined,
  destType: LocationType | null | undefined,
): LocationAdjustments {
  if (!items || items.length === 0) {
    return { originSurcharge: 0, originDiscount: 0, destSurcharge: 0, destDiscount: 0 };
  }

  const discountMap = calculateMultiItemDiscounts(
    items.map((i) => ({ uid: i.uid, basePrice: i.basePrice })),
  );

  let insideSum = 0;
  let parkingSum = 0;
  for (const item of items) {
    const rate = discountMap.get(item.uid)?.discountRate ?? 0;
    const factor = 1 - rate;
    insideSum += Math.round((item.locationInsideExtra || 0) * factor);
    parkingSum += Math.round((item.locationParkingDiscount || 0) * factor);
  }

  return {
    originSurcharge: originType === 'INSIDE' ? insideSum : 0,
    originDiscount: originType === 'PARKING' ? parkingSum : 0,
    destSurcharge: destType === 'INSIDE' ? insideSum : 0,
    destDiscount: destType === 'PARKING' ? parkingSum : 0,
  };
}

/* ── 가격 계산 ── */

interface PriceInput {
  itemBasePrice: number;
  /** 직선거리(haversine) km — 티어별 도로거리/요율 계산 입력 */
  straightKm: number;
  originHasElevator: boolean;
  originFloor: number;
  destHasElevator: boolean;
  destFloor: number;
  /** 수거/배송 위치 유형 보정 (없으면 0) */
  locationAdjustments?: LocationAdjustments;
  /** 거리할증을 외부에서 직접 지정 (관리자 임시 입력에서 도로거리 직접 사용 시) */
  distanceSurchargeOverride?: number;
}

export function calculatePrice(input: PriceInput): PriceBreakdown {
  const {
    itemBasePrice,
    straightKm,
    originHasElevator,
    originFloor,
    destHasElevator,
    destFloor,
    locationAdjustments,
  } = input;

  // 1. 거리할증: 외부 override 가 있으면 그대로, 없으면 직선거리 티어별 (≤10km 2,000원, ≤20km 1,600원, >20km 1,200원), 3km 도로거리 무료
  const distanceSurcharge = input.distanceSurchargeOverride ?? calcDistanceSurcharge(straightKm).surcharge;
  const distanceSurchargeRate = 0; // 정액제 — 비율 사용 안 함

  // 2. 계단할증 (출발지): 엘리베이터 유무와 무관하게 입력된 층수 기준 (0.5층당 4%, 0=계단없음)
  const safeOriginFloor = originFloor <= -2 ? 0 : originFloor; // -99(미선택)·-10(모름) 등 방어
  const stairStepsOrigin = safeOriginFloor < 0 ? Math.abs(safeOriginFloor) : Math.max(0, safeOriginFloor);
  const stairSurchargeOriginRate = stairStepsOrigin * 0.08;

  // 3. 계단할증 (도착지)
  const safeDestFloor = destFloor <= -2 ? 0 : destFloor; // -99(미선택)·-10(모름) 등 방어
  const stairStepsDest = safeDestFloor < 0 ? Math.abs(safeDestFloor) : Math.max(0, safeDestFloor);
  const stairSurchargeDestRate = stairStepsDest * 0.08;

  // 4. 계단할증 금액
  const stairSurchargeOrigin = Math.round(itemBasePrice * stairSurchargeOriginRate);
  const stairSurchargeDest = Math.round(itemBasePrice * stairSurchargeDestRate);

  // 5. 위치 유형 보정 (집안 추가 / 주차장 할인)
  const locationOriginSurcharge = locationAdjustments?.originSurcharge ?? 0;
  const locationOriginDiscount = locationAdjustments?.originDiscount ?? 0;
  const locationDestSurcharge = locationAdjustments?.destSurcharge ?? 0;
  const locationDestDiscount = locationAdjustments?.destDiscount ?? 0;

  // 6. 합산: 물품가 × (1 + 계단출발) × (1 + 계단도착) + 거리정액 + 위치보정 → 100원 단위 반올림
  const beforeDiscount = Math.round(
    itemBasePrice *
      (1 + stairSurchargeOriginRate) *
      (1 + stairSurchargeDestRate) +
      distanceSurcharge +
      locationOriginSurcharge +
      locationDestSurcharge -
      locationOriginDiscount -
      locationDestDiscount
  );

  const totalPrice = Math.round(beforeDiscount / 100) * 100;

  return {
    itemBasePrice,
    distanceSurchargeRate,
    distanceSurcharge,
    stairSurchargeOriginRate,
    stairSurchargeOrigin,
    stairSurchargeDestRate,
    stairSurchargeDest,
    locationOriginSurcharge,
    locationOriginDiscount,
    locationDestSurcharge,
    locationDestDiscount,
    totalPrice,
  };
}

/* ── 저가 물품 (기본가 16,000원 이하) 규칙 ── */

/** 저가 물품 판정 기준 (다물품 할인 후 물품 기본가 합계 / 견적 approvedPrice) */
export const LOW_PRICE_ITEM_THRESHOLD = 16000;
/** 저가 물품 수거시간 지정 할증 */
export const LOW_PRICE_DESIGNATED_PICKUP_SURCHARGE = 4000;
/** 저가 물품 유연시간 1개만 선택 시 할증 */
export const LOW_PRICE_SINGLE_FLEX_SURCHARGE = 2000;

/** itemBasePrice 는 다물품 할인 적용 후 합계 (priceBreakdown.itemBasePrice / 견적 approvedPrice) */
export function isLowPriceItem(itemBasePrice: number): boolean {
  return itemBasePrice > 0 && itemBasePrice <= LOW_PRICE_ITEM_THRESHOLD;
}

export interface ScheduleChargeInput {
  /** 기본 운송비 (priceBreakdown.totalPrice) */
  baseTotal: number;
  /** 다물품 할인 후 물품 기본가 합계 — 저가 물품 판정 기준 */
  itemBasePrice: number;
  /** 수거 유연시간 선택 수 (0~2) */
  flexCount: number;
  /** 수거 슬롯 지정 여부 (designatedPickupSlot 존재) */
  hasDesignatedPickup: boolean;
  /** 배송 슬롯 지정 여부 */
  hasDesignatedDelivery: boolean;
  /** 최적슬롯 할인율 (저가 물품이면 무시) */
  pickupDiscountRate?: number;
  deliveryDiscountRate?: number;
}

export interface ScheduleChargeBreakdown {
  flexDiscount: number;
  slotDiscount: number;
  deliverySurcharge: number;
  /** 저가 물품 + 수거슬롯 지정 → 4,000원 */
  designatedPickupSurcharge: number;
  /** 저가 물품 + 유연 1개만 선택 → 2,000원 */
  singleFlexSurcharge: number;
  finalPrice: number;
  isLowPrice: boolean;
}

/**
 * 일정 관련 할인/할증 통합 계산.
 *
 * 일반 물품: 유연 할인(5%×n) + 최적슬롯 할인 - 도착시간 할증(7%) → 기존 calcFinalPrice 와 동일.
 * 저가 물품 (기본가 ≤ 16,000원): 유연시간(오전+오후)이 기본 — 유연/최적슬롯 할인 없음.
 *   수거슬롯 지정 시 +4,000원, 유연 1개만 선택 시 +2,000원. 도착시간 7% 할증은 동일 적용.
 */
export function calcScheduleCharges(input: ScheduleChargeInput): ScheduleChargeBreakdown {
  const { baseTotal, itemBasePrice, flexCount, hasDesignatedPickup, hasDesignatedDelivery } = input;
  const lowPrice = isLowPriceItem(itemBasePrice);

  // 도착시간 지정 7% 할증 — 저가 여부와 무관
  const deliverySurcharge = calcDeliveryTimeSurcharge(baseTotal, hasDesignatedDelivery);

  if (lowPrice) {
    // 유연 할인 없음, 최적슬롯 할인 없음
    const designatedPickupSurcharge = hasDesignatedPickup ? LOW_PRICE_DESIGNATED_PICKUP_SURCHARGE : 0;
    // 수거슬롯 지정 시 유연 할증은 미적용 (슬롯이 우선). 유연 1개만 +2,000, 둘 다면 0.
    const singleFlexSurcharge =
      !hasDesignatedPickup && flexCount === 1 ? LOW_PRICE_SINGLE_FLEX_SURCHARGE : 0;
    const finalPrice = Math.round(
      (baseTotal + deliverySurcharge + designatedPickupSurcharge + singleFlexSurcharge) / 100
    ) * 100;
    return {
      flexDiscount: 0,
      slotDiscount: 0,
      deliverySurcharge,
      designatedPickupSurcharge,
      singleFlexSurcharge,
      finalPrice,
      isLowPrice: true,
    };
  }

  // 일반 물품 — 기존 동작 그대로
  const flexDiscount = calcFlexDiscount(baseTotal, flexCount);
  const slotDiscount = calcSlotDiscount(baseTotal, input.pickupDiscountRate ?? 0, input.deliveryDiscountRate ?? 0);
  const finalPrice = Math.round((baseTotal - flexDiscount - slotDiscount + deliverySurcharge) / 100) * 100;
  return {
    flexDiscount,
    slotDiscount,
    deliverySurcharge,
    designatedPickupSurcharge: 0,
    singleFlexSurcharge: 0,
    finalPrice,
    isLowPrice: false,
  };
}

/** 유연시간 할인 계산 (수거 오전/오후 각 5%, 최대 2개 = 10%) */
export function calcFlexDiscount(baseTotal: number, flexCount: number): number {
  const clampedCount = Math.min(Math.max(flexCount, 0), 2);
  return Math.round(baseTotal * clampedCount * 0.05);
}

/** 도착시간 지정 할증 (특정 배송 슬롯 선택 시 7%) */
export function calcDeliveryTimeSurcharge(baseTotal: number, hasDesignatedDelivery: boolean): number {
  return hasDesignatedDelivery ? Math.round(baseTotal * 0.07) : 0;
}

/** 최적 슬롯 할인 (경로 최적화 기반, 수거/배송 각각 0~10%) */
export function calcSlotDiscount(baseTotal: number, pickupRate: number, deliveryRate: number): number {
  return Math.round(baseTotal * pickupRate / 100) + Math.round(baseTotal * deliveryRate / 100);
}

/** 최종 결제 금액 (기본 운송비 - 유연할인 - 슬롯할인 + 도착시간 할증, 100원 단위 반올림) */
export function calcFinalPrice(baseTotal: number, flexCount: number, hasDesignatedDelivery: boolean = false, pickupDiscountRate: number = 0, deliveryDiscountRate: number = 0): number {
  return Math.round((baseTotal - calcFlexDiscount(baseTotal, flexCount) - calcSlotDiscount(baseTotal, pickupDiscountRate, deliveryDiscountRate) + calcDeliveryTimeSurcharge(baseTotal, hasDesignatedDelivery)) / 100) * 100;
}

/** @deprecated 하위 호환 */
export function calcDesignatedSurcharge(_baseTotal: number, _slotCount: number): number {
  return 0;
}
